import Handlebars from "handlebars";
import {
  type Context,
  defaultOperators,
  Liquid,
  type Operators,
} from "@hightouch/lib/templating/liquid";

import {
  normalizeBrazeAttributeRefs,
  repairMangledLiquid,
} from "@hightouch/lib/llm/braze-template-refs";
import { maskDjangoProviderTags } from "../esp-templating/django-tag-mask";
import {
  maskColonNamespacedOutputTags,
  restoreProviderTags,
} from "../esp-templating/provider-tag-mask";
import { twigToLiquid } from "../esp-templating/twig-to-liquid";
import { type ComparisonOperator, compareValues } from "./preview-variables";
import { containsTwig, renderTwig } from "./twig";

export type PersonalizationPrimitive = string | number | boolean;
export type PersonalizationTemplateLanguage = "LIQUID" | "HANDLEBARS" | "TWIG";

export interface PersonalizationRenderSource {
  subject?: string | null;
  preheader?: string | null;
  html: string;
}

export interface RenderedPersonalizationSource {
  subject: string | null;
  preheader: string | null;
  html: string;
}

export type PersonalizationField =
  | "subject"
  | "preheader"
  | "html"
  | "body"
  | "title";

export interface PersonalizationRenderError {
  /** Which part of the message failed to render. */
  field: PersonalizationField;
  /** The underlying template-engine error message. */
  message: string;
}

// This is a deliberately small preview renderer, not a full ESP templating
// engine. Liquid is the default Hightouch template style, but some customers
// provide workspace guidance for provider-specific Handlebars variants.
// The backend tells us which language to use; the frontend only renders the
// returned sample values.
const HTML_ESCAPES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#x27;": "'",
  "&#x60;": "`",
  "&#x3D;": "=",
};

// Reverses `Handlebars.escapeExpression`, which HTML mode applies to sample values.
function unescapeSampleValue(value: unknown): unknown {
  return typeof value === "string"
    ? value.replace(/&(?:amp|lt|gt|quot|#x27|#x60|#x3D);/g, (entity) => {
        return HTML_ESCAPES[entity] ?? entity;
      })
    : value;
}

// HTML mode stores escaped sample values so every output is safe, so conditions
// have to compare the text a recipient's attribute would hold.
const UNESCAPED_COMPARISONS = ["==", "!=", ">", "<", ">=", "<=", "contains"];
const previewOperators: Operators = { ...defaultOperators };
for (const name of UNESCAPED_COMPARISONS) {
  const compare = defaultOperators[name];
  if (!compare) continue;
  const binary = compare as (
    lhs: unknown,
    rhs: unknown,
    ctx: Context,
  ) => boolean;
  previewOperators[name] = (lhs: unknown, rhs: unknown, ctx: Context) =>
    binary(unescapeSampleValue(lhs), unescapeSampleValue(rhs), ctx);
}

const liquid = new Liquid({
  strictFilters: false,
  strictVariables: false,
  lenientIf: true,
  operators: previewOperators,
});

const handlebars = Handlebars.create();
handlebars.registerHelper("helperMissing", () => "");

// The LLM that drives `personalizationPreview.language` sometimes classifies a
// template as Handlebars when its body uses Liquid-style filters like
// `{{ first_name | capitalize }}` (because of unrelated Handlebars-y idioms
// like `{{this.foo}}`). Handlebars only sees `{{capitalize first_name}}` — and
// with no helper registered, it falls through to `helperMissing` and renders
// as an empty string. Register Liquid-equivalent helpers for the same filter
// set we promise on the Liquid side so the preview matches either way.
function liquidToString(value: unknown): string {
  if (value == null) return "";
  if (
    typeof value === "object" &&
    value !== null &&
    "hash" in value &&
    "name" in value
  ) {
    // Handlebars passes its options object as the trailing argument; if the
    // template called the helper with no preceding value, `value` is that
    // options object and should be treated as missing.
    return "";
  }
  return String(value);
}

handlebars.registerHelper("capitalize", (value: unknown) => {
  const s = liquidToString(value);
  return s.length === 0
    ? ""
    : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
});
handlebars.registerHelper("upcase", (value: unknown) =>
  liquidToString(value).toUpperCase(),
);
handlebars.registerHelper("downcase", (value: unknown) =>
  liquidToString(value).toLowerCase(),
);
// Aliases for Braze-flavored Handlebars templates that use the case-helper
// names instead of the Liquid filter names.
handlebars.registerHelper("upper", (value: unknown) =>
  liquidToString(value).toUpperCase(),
);
handlebars.registerHelper("lower", (value: unknown) =>
  liquidToString(value).toLowerCase(),
);
handlebars.registerHelper("capitalizeFirst", (value: unknown) => {
  const s = liquidToString(value);
  return s.length === 0 ? "" : s.charAt(0).toUpperCase() + s.slice(1);
});
handlebars.registerHelper("strip", (value: unknown) =>
  liquidToString(value).trim(),
);
handlebars.registerHelper("lstrip", (value: unknown) =>
  liquidToString(value).replace(/^\s+/, ""),
);
handlebars.registerHelper("rstrip", (value: unknown) =>
  liquidToString(value).replace(/\s+$/, ""),
);
handlebars.registerHelper("default", (value: unknown, fallback: unknown) => {
  if (value == null || value === false || value === "") {
    return liquidToString(fallback);
  }
  return liquidToString(value);
});
handlebars.registerHelper(
  "replace",
  (value: unknown, from: unknown, to: unknown) =>
    liquidToString(value).split(liquidToString(from)).join(liquidToString(to)),
);

// Iterable-style comparisons, usable as a subexpression (`{{#if (gte count 5)}}`)
// or as a block (`{{#eq tier "gold"}}…{{else}}…{{/eq}}`).
const HANDLEBARS_COMPARISONS: Record<string, ComparisonOperator> = {
  eq: "==",
  ne: "!=",
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
};
for (const [name, operator] of Object.entries(HANDLEBARS_COMPARISONS)) {
  handlebars.registerHelper(
    name,
    function comparisonHelper(
      this: unknown,
      left: unknown,
      right: unknown,
      options: Handlebars.HelperOptions,
    ) {
      const result = compareValues(left, right, operator, {
        ignoreCase: false,
      });
      if (typeof options?.fn !== "function") return result;
      return result ? options.fn(this) : options.inverse(this);
    },
  );
}

// `{{now format="YYYY"}}` is a common pattern for footer copyright years and
// other "today" stamps in customer templates (Iterable, Braze-flavored). Without
// this helper Handlebars treats `{{now hash=...}}` as a helper invocation, hits
// the truthy scope value of `now` if any, and throws. Register a real helper so
// the preview renders the same year the ESP would. Supports the common Moment-
// flavored tokens that show up in marketing copy; unknown tokens pass through.
const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTHS_SHORT = MONTHS_LONG.map((m) => m.slice(0, 3));
const DAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const DAYS_SHORT = DAYS_LONG.map((d) => d.slice(0, 3));

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatDateWithMomentTokens(d: Date, format: string): string {
  // Tokens are matched longest-first so `YYYY` wins over `YY` and `MMMM` over
  // `MMM`/`MM`/`M`. Both upper- and lowercase year/day variants are accepted
  // since customer templates mix Moment (`YYYY`/`DD`) and Java-style
  // (`yyyy`/`dd`) tokens interchangeably.
  const tokenHandlers: Array<[string, () => string]> = [
    ["YYYY", () => String(d.getFullYear())],
    ["yyyy", () => String(d.getFullYear())],
    ["YY", () => String(d.getFullYear()).slice(-2)],
    ["yy", () => String(d.getFullYear()).slice(-2)],
    ["MMMM", () => MONTHS_LONG[d.getMonth()] ?? ""],
    ["MMM", () => MONTHS_SHORT[d.getMonth()] ?? ""],
    ["MM", () => pad2(d.getMonth() + 1)],
    ["M", () => String(d.getMonth() + 1)],
    ["DD", () => pad2(d.getDate())],
    ["dd", () => pad2(d.getDate())],
    ["D", () => String(d.getDate())],
    ["dddd", () => DAYS_LONG[d.getDay()] ?? ""],
    ["ddd", () => DAYS_SHORT[d.getDay()] ?? ""],
    ["HH", () => pad2(d.getHours())],
    ["H", () => String(d.getHours())],
    ["hh", () => pad2(((d.getHours() + 11) % 12) + 1)],
    ["h", () => String(((d.getHours() + 11) % 12) + 1)],
    ["mm", () => pad2(d.getMinutes())],
    ["ss", () => pad2(d.getSeconds())],
    ["A", () => (d.getHours() >= 12 ? "PM" : "AM")],
    ["a", () => (d.getHours() >= 12 ? "pm" : "am")],
  ];
  const pattern = new RegExp(tokenHandlers.map(([t]) => t).join("|"), "g");
  return format.replace(pattern, (match) => {
    const found = tokenHandlers.find(([t]) => t === match);
    return found ? found[1]() : match;
  });
}

handlebars.registerHelper("now", (options: Handlebars.HelperOptions) => {
  const format = options?.hash?.format;
  const d = new Date();
  return typeof format === "string" && format.length > 0
    ? formatDateWithMomentTokens(d, format)
    : d.toISOString();
});

// Custom `{{#catalog 'NAME' LOOKUP_KEY}}…{{else}}…{{/catalog}}` block helper.
// In production (e.g. Braze) this looks up a row from an ESP-side catalog
// table; here we render against the LLM-generated `sampleRows`. Behavior:
//
// - No sample rows OR no lookup key (null/undefined/"") → render the
//   `{{else}}` branch. This matches what an ESP does at send time when the
//   marketer hasn't filled in the lookup value: the catalog lookup misses,
//   so the fallback content runs.
// - Lookup key matches a row by `id` / `pk` / `primaryKey` → use that row.
// - Lookup key provided but doesn't match → fall back to the first row so
//   the preview shows realistic content rather than empty fallback text.
type CatalogRow = Record<string, PersonalizationPrimitive>;
type CatalogSamples = Record<string, { sampleRows: CatalogRow[] }>;

function findMatchingCatalogRow(
  rows: readonly CatalogRow[],
  lookupKey: unknown,
): CatalogRow {
  const needle = String(lookupKey);
  const match = rows.find((row) => {
    const id = row.id ?? row.pk ?? row.primaryKey;
    return id != null && String(id) === needle;
  });
  return match ?? rows[0]!;
}

handlebars.registerHelper(
  "catalog",
  function catalogHelper(
    this: unknown,
    catalogName: unknown,
    lookupKey: unknown,
    options: Handlebars.HelperOptions,
  ) {
    const catalogs = options.data?.root?._catalogs as
      | CatalogSamples
      | undefined;
    const rows = catalogs?.[String(catalogName)]?.sampleRows ?? [];
    if (rows.length === 0 || lookupKey == null || lookupKey === "") {
      return options.inverse ? options.inverse(this) : "";
    }
    return options.fn(findMatchingCatalogRow(rows, lookupKey));
  },
);

export interface PersonalizationCatalogSample {
  name: string;
  sampleRows: Array<Record<string, PersonalizationPrimitive>>;
}

/**
 * Receives a render failure. `failingTag` is the source text of the tag the
 * engine rejected, when it reports one, so a caller can drop exactly that tag.
 */
export type RenderErrorHandler = (message: string, failingTag?: string) => void;

function failingTagText(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("token" in error)) {
    return undefined;
  }
  const { token } = error;
  if (typeof token !== "object" || token === null || !("getText" in token)) {
    return undefined;
  }
  const text = typeof token.getText === "function" ? token.getText() : null;
  return typeof text === "string" ? text : undefined;
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// Bridge the field-agnostic `onError` callback used by the low-level renderers
// to the field-tagged error sink the callers collect. Returns undefined (a
// no-op) when no sink was supplied so rendering stays a pure pass-through.
export function collectError(
  errors: PersonalizationRenderError[] | undefined,
  field: PersonalizationField,
): ((message: string) => void) | undefined {
  return errors ? (message) => errors.push({ field, message }) : undefined;
}

export function renderPersonalizationSource(
  source: PersonalizationRenderSource,
  values: Record<string, PersonalizationPrimitive>,
  language: PersonalizationTemplateLanguage,
  catalogs: PersonalizationCatalogSample[] = [],
  errors?: PersonalizationRenderError[],
): RenderedPersonalizationSource {
  return {
    subject:
      source.subject == null
        ? null
        : renderPersonalizationText(
            source.subject,
            values,
            language,
            catalogs,
            collectError(errors, "subject"),
          ),
    preheader:
      source.preheader == null
        ? null
        : renderPersonalizationText(
            source.preheader,
            values,
            language,
            catalogs,
            collectError(errors, "preheader"),
          ),
    html: renderPersonalizationHtml(
      source.html,
      values,
      language,
      catalogs,
      collectError(errors, "html"),
    ),
  };
}

export function renderPersonalizationText(
  template: string,
  values: Record<string, PersonalizationPrimitive>,
  language: PersonalizationTemplateLanguage,
  catalogs: PersonalizationCatalogSample[] = [],
  onError?: RenderErrorHandler,
): string {
  return renderTemplate(template, values, language, false, catalogs, onError);
}

export function renderPersonalizationHtml(
  template: string,
  values: Record<string, PersonalizationPrimitive>,
  language: PersonalizationTemplateLanguage,
  catalogs: PersonalizationCatalogSample[] = [],
  onError?: RenderErrorHandler,
): string {
  return renderTemplate(template, values, language, true, catalogs, onError);
}

const LIQUID_FILTER_RE = /\|\s*[A-Za-z_]\w*\s*:/;
const LIQUID_ONLY_TAG_RE =
  /^\{%-?\s*(?:assign|elsif|unless|endunless|case|when|endcase|capture|endcapture|comment|endcomment|raw|endraw|render|liquid|tablerow|endtablerow|cycle|increment|decrement|echo)\b/;

function renderTemplate(
  template: string,
  values: Record<string, PersonalizationPrimitive>,
  language: PersonalizationTemplateLanguage,
  escapeValues: boolean,
  catalogs: PersonalizationCatalogSample[],
  onError?: RenderErrorHandler,
): string {
  // Xtremepush templates are Twig, which the backend still labels LIQUID.
  if (language !== "TWIG" && !containsTwig(template)) {
    return renderLiquidOrHandlebars(
      template,
      values,
      language,
      escapeValues,
      catalogs,
      onError,
    );
  }
  const twig = renderTwig(template, {
    scope: buildTemplateScope(values, false, "TWIG", catalogs),
    escapeHtml: escapeValues,
  });
  // Only a skipped Liquid tag or filter (`| name:`) is worth the Liquid path,
  // and Handlebars ignores `{% %}`, so its fallback would print every Twig branch.
  const skippedLiquid = twig.skippedTags.some(
    (tag) => LIQUID_FILTER_RE.test(tag) || LIQUID_ONLY_TAG_RE.test(tag),
  );
  if (language !== "LIQUID") {
    if (twig.error !== null) onError?.(twig.error);
    return twig.output;
  }
  if (twig.error === null && !skippedLiquid) return twig.output;
  // A template can mix one Twig-only marker into Liquid, vendor tags or
  // Liquid filters the Twig engine cannot read, so the Liquid path gets a turn.
  const fallbackErrors: Array<[string, string | undefined]> = [];
  const fallback = renderLiquidOrHandlebars(
    template,
    values,
    language,
    escapeValues,
    catalogs,
    (message, failingTag) => fallbackErrors.push([message, failingTag]),
  );
  if (fallbackErrors.length === 0) return fallback;
  if (twig.error === null) return twig.output;
  for (const [message, failingTag] of fallbackErrors) {
    onError?.(message, failingTag);
  }
  return template;
}

function renderLiquidOrHandlebars(
  template: string,
  values: Record<string, PersonalizationPrimitive>,
  language: Exclude<PersonalizationTemplateLanguage, "TWIG">,
  escapeValues: boolean,
  catalogs: PersonalizationCatalogSample[],
  onError?: RenderErrorHandler,
): string {
  // Vendor-resolved tags the engines can't parse are masked up front and
  // restored byte-for-byte after the render: Klaviyo `{% web_view %}` tags and
  // colon-namespaced output tags like SES's `{{ses:openTracker}}`, either of
  // which would otherwise fail the Liquid parse of everything around them.
  // Handlebars skips both masks: it tokenizes `ses:openTracker` as a plain
  // identifier, so neither tag breaks its parse.
  const masked =
    language === "LIQUID"
      ? maskDjangoProviderTags(template)
      : {
          maskedTemplate: template,
          placeholders: new Map<string, string>(),
        };
  const colonMasked =
    language === "LIQUID"
      ? maskColonNamespacedOutputTags(masked.maskedTemplate)
      : {
          maskedTemplate: masked.maskedTemplate,
          placeholders: new Map<string, string>(),
        };
  const placeholders = new Map([
    ...masked.placeholders,
    ...colonMasked.placeholders,
  ]);
  const lookupPrepared = prepareDjangoLookups(
    colonMasked.maskedTemplate,
    values,
  );
  const restoreTags = (rendered: string): string =>
    restoreProviderTags(rendered, placeholders);
  const scopeValues = { ...values, ...lookupPrepared.values };
  const renderWithLiquid = (input: string): string =>
    liquid.parseAndRenderSync(
      input,
      buildTemplateScope(scopeValues, escapeValues, "LIQUID", catalogs),
    );
  const renderOnce = (input: string): string => {
    if (language === "HANDLEBARS") {
      return renderHandlebarsTolerant(
        input,
        buildTemplateScope(scopeValues, false, language, catalogs),
        { noEscape: !escapeValues, strict: false },
      );
    }
    return renderWithLiquid(input);
  };

  // Braze-authored templates carry `${…}` attribute refs that neither
  // engine can parse — one ref anywhere (even in an HTML comment) would
  // otherwise fail the whole parse and drop the preview back to raw HTML.
  // Rewritten for rendering only; the fallback below still returns the
  // customer's original text.
  const normalized = normalizeBrazeAttributeRefs(lookupPrepared.template);
  try {
    return restoreTags(renderOnce(normalized));
  } catch (error) {
    // Second chance: templates mangled by an HTML/rich-text editor (smart
    // quotes or entity-escaped operators inside Liquid delimiters) are
    // repaired only after a normal parse has failed, so a healthy template
    // that legitimately contains those characters is never rewritten.
    const repaired = repairMangledLiquid(normalized);
    if (repaired !== normalized) {
      try {
        return restoreTags(renderOnce(repaired));
      } catch {
        // Fall through to the next, weaker strategy.
      }
    }
    // Xtremepush templates are Twig, which arrives as LIQUID (the closest
    // dialect this renderer speaks) — but one `|default('x')`, `{% elseif %}`
    // or `{% set %}` anywhere fails the whole Liquid parse and blanks every
    // tag in the field. Rewrite the Twig constructs Liquid can express and
    // retry; healthy Liquid never reaches this branch.
    const twigNormalized = twigToLiquid(repaired);
    if (twigNormalized !== repaired) {
      try {
        return restoreTags(renderOnce(twigNormalized));
      } catch {
        // Fall through to the next, weaker strategy.
      }
    }
    if (language === "HANDLEBARS") {
      // HANDLEBARS is often pinned from the ESP dialect (Iterable) rather
      // than derived from the template text, and generated bodies routinely
      // carry Liquid pipe filters (`{{ FIRST_NAME | default: "there" }}`)
      // that Handlebars can't even tokenize — `|` lexes as block params, so
      // the whole-template parse throws and per-statement tolerance never
      // gets a chance. A template that fails the Handlebars *parse* outright
      // is very often plain Liquid, so try that before falling back to raw
      // text. The colon-namespaced vendor mask applies here for the same
      // reason it does on the native Liquid path.
      try {
        const fallbackMasked = maskColonNamespacedOutputTags(twigNormalized);
        return restoreTags(
          restoreProviderTags(
            renderWithLiquid(fallbackMasked.maskedTemplate),
            fallbackMasked.placeholders,
          ),
        );
      } catch {
        // Report the original, pre-fallback failure below.
      }
    }
    onError?.(toErrorMessage(error), failingTagText(error));
    return template;
  }
}

// Anchored on the output-tag head so a `x | lookup: "y"` written in ordinary
// prose is never rewritten into a synthetic key, and bounded so the identifier
// run can't backtrack quadratically over a long word in a large template.
const DJANGO_LOOKUP_RE =
  /(\{\{-?\s*)([A-Za-z_][A-Za-z0-9_.]{0,200})\s*\|\s*lookup\s*:\s*(?:"([^"]+)"|'([^']+)')/g;

function prepareDjangoLookups(
  template: string,
  values: Record<string, PersonalizationPrimitive>,
): {
  template: string;
  values: Record<string, PersonalizationPrimitive>;
} {
  const syntheticValues: Record<string, PersonalizationPrimitive> = {};
  let index = 0;
  const prepared = template.replace(
    DJANGO_LOOKUP_RE,
    (
      _match,
      openDelimiter: string,
      inputPath: string,
      doubleKey?: string,
      singleKey?: string,
    ) => {
      const key = doubleKey ?? singleKey ?? "";
      const syntheticKey = `__ht_django_lookup_${index++}`;
      if (
        !hasPrototypePollutingSegment(inputPath) &&
        !hasPrototypePollutingSegment(key)
      ) {
        const nestedKey = `${inputPath}.${key}`;
        if (Object.hasOwn(values, nestedKey)) {
          syntheticValues[syntheticKey] = values[nestedKey]!;
        } else if (Object.hasOwn(values, key)) {
          syntheticValues[syntheticKey] = values[key]!;
        }
      }
      return `${openDelimiter}${syntheticKey}`;
    },
  );
  return { template: prepared, values: syntheticValues };
}

// Render Handlebars statement-by-statement so a single broken expression
// (e.g. `{{now format="YYYY"}}` where `now` is a scope string — Handlebars
// treats this as a helper call and throws when the value isn't callable)
// doesn't blank out the entire preview. We parse the template into an AST,
// then compile each top-level body node against the same scope; failures are
// swallowed at the node level. Block bodies are still all-or-nothing — a
// throw inside `{{#each}}` collapses the whole block, which is acceptable
// for a preview.
function renderHandlebarsTolerant(
  template: string,
  scope: Record<string, unknown>,
  compileOptions: { noEscape: boolean; strict: boolean },
): string {
  // A whole-template parse failure throws to the caller, which owns the
  // repaired-retry and raw-template fallback. Per-node compile throws below
  // are deliberately swallowed — those degrade gracefully (neighboring
  // statements still render) and are not "unable to parse" failures.
  const ast: hbs.AST.Program = Handlebars.parse(template);

  const lineOffsets = buildLineOffsets(template);
  let output = "";
  for (const node of ast.body) {
    if (node.type === "ContentStatement") {
      output += (node as hbs.AST.ContentStatement).value;
      continue;
    }
    if (node.type === "CommentStatement") continue;
    if (!node.loc) continue;

    const source = template.slice(
      locToOffset(node.loc.start, lineOffsets),
      locToOffset(node.loc.end, lineOffsets),
    );
    try {
      output += handlebars.compile(source, compileOptions)(scope);
    } catch {
      // Swallow per-node so neighboring statements still render.
    }
  }
  return output;
}

function buildLineOffsets(source: string): number[] {
  const offsets = [0];
  for (let i = 0; i < source.length; i++) {
    if (source.charCodeAt(i) === 10 /* \n */) offsets.push(i + 1);
  }
  return offsets;
}

function locToOffset(
  loc: { line: number; column: number },
  lineOffsets: number[],
): number {
  return (lineOffsets[loc.line - 1] ?? 0) + loc.column;
}

// Keys that would let a crafted variable name walk into an object's prototype
// chain. `values` can carry client-supplied names (LLM-inferred or edited in
// the preview, and — for the PNG export — passed straight from the browser),
// and this scope is built in a shared, multi-tenant worker, so a name like
// `__proto__.x` must never reach the nested-assignment walk below or it would
// pollute `Object.prototype` process-wide.
const PROTOTYPE_POLLUTING_KEYS = new Set([
  "__proto__",
  "prototype",
  "constructor",
]);

function hasPrototypePollutingSegment(key: string): boolean {
  return key
    .split(".")
    .some((segment) => PROTOTYPE_POLLUTING_KEYS.has(segment));
}

function buildTemplateScope(
  values: Record<string, PersonalizationPrimitive>,
  escapeValues: boolean,
  language: PersonalizationTemplateLanguage,
  catalogs: PersonalizationCatalogSample[],
): Record<string, unknown> {
  const scope: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    // A variable name can never legitimately reference the prototype chain;
    // skip it entirely rather than render it (the template's reference to it
    // falls through to the missing-variable path, same as any unknown key).
    if (hasPrototypePollutingSegment(key)) {
      continue;
    }

    const normalizedValue =
      language === "LIQUID" && value === "" ? undefined : value;
    // Only strings can carry markup; escaping a number or boolean would turn it
    // into a string and break comparisons like `{% if flag == true %}`.
    const renderedValue =
      escapeValues && typeof normalizedValue === "string"
        ? Handlebars.escapeExpression(normalizedValue)
        : normalizedValue;
    scope[key] = renderedValue;

    const parts = key.split(".");
    if (parts.length <= 1) continue;

    let current = scope;
    for (const [index, part] of parts.entries()) {
      if (!part) break;
      if (index === parts.length - 1) {
        current[part] = renderedValue;
        break;
      }
      const next = current[part];
      if (!next || typeof next !== "object" || Array.isArray(next)) {
        current[part] = {};
      }
      current = current[part] as Record<string, unknown>;
    }
  }

  if (catalogs.length > 0) {
    // Reserved underscore-prefixed key avoids collisions with agent-defined
    // variable names. The `catalog` block helper reads it via
    // `options.data.root._catalogs`.
    scope._catalogs = Object.fromEntries(
      catalogs.map((catalog) => [
        catalog.name,
        { sampleRows: catalog.sampleRows },
      ]),
    );
  }

  return scope;
}
